/**
 * computeStaticPeaks — DAW-style min/max peak envelope decimation.
 *
 * Given a decoded AudioBuffer, this computes a fixed-width peak summary
 * (min + max per column) suitable for rendering a static overview waveform
 * like a Reaper / Pro Tools / Ableton track item.
 *
 * Algorithm:
 *   1. Mix all channels down to mono (average).
 *   2. Divide the total sample count by the target number of columns.
 *   3. For each column bucket, scan every sample and record the min & max.
 *
 * The result array has exactly `columns` entries.
 * Each entry satisfies  -1 ≤ min ≤ max ≤ 1  for normalized audio.
 *
 * Complexity: O(totalSamples) — each sample is visited exactly once.
 * Memory:     O(totalSamples) for the mono mixdown + O(columns) for output.
 */

export interface PeakPair {
  min: number;
  max: number;
}

/**
 * Compute the static peak envelope from an AudioBuffer.
 *
 * @param buffer  Decoded AudioBuffer (mono or multi-channel).
 * @param columns Number of output peak buckets (= display width in px).
 * @returns       Array of { min, max } pairs, length === columns.
 */
export function computeStaticPeaks(buffer: AudioBuffer, columns: number): PeakPair[] {
  const cols = Math.max(1, Math.round(columns));
  const numChannels = buffer.numberOfChannels;
  const length = buffer.length;

  if (length === 0 || numChannels === 0) {
    return Array.from({ length: cols }, () => ({ min: 0, max: 0 }));
  }

  // ── Step 1: Mono mixdown ──────────────────────────────────────
  // For single-channel buffers we can skip the copy.
  let mono: Float32Array;
  if (numChannels === 1) {
    mono = buffer.getChannelData(0);
  } else {
    mono = new Float32Array(length);
    for (let ch = 0; ch < numChannels; ch++) {
      const data = buffer.getChannelData(ch);
      for (let i = 0; i < length; i++) {
        mono[i] += data[i];
      }
    }
    const inv = 1 / numChannels;
    for (let i = 0; i < length; i++) {
      mono[i] *= inv;
    }
  }

  // ── Step 2: Bucket decimation ─────────────────────────────────
  const peaks: PeakPair[] = new Array(cols);
  const samplesPerCol = length / cols;

  for (let col = 0; col < cols; col++) {
    const start = Math.floor(col * samplesPerCol);
    const end = col === cols - 1 ? length : Math.floor((col + 1) * samplesPerCol);
    const safeEnd = Math.max(start + 1, Math.min(end, length));

    let lo = Infinity;
    let hi = -Infinity;
    for (let i = start; i < safeEnd; i++) {
      const v = mono[i];
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    if (lo === Infinity) { lo = 0; hi = 0; }
    peaks[col] = { min: lo, max: hi };
  }

  return peaks;
}

/**
 * Compute RMS (root mean square) per bucket alongside peaks.
 * Useful for showing a filled "RMS body" behind the peak outline
 * (standard DAW waveform rendering — peaks are the outline whiskers,
 * RMS is the solid body).
 */
export interface PeakRmsPair extends PeakPair {
  rms: number; // 0..1 (absolute, always positive)
}

export interface StereoPeakRms {
  left: PeakRmsPair[];
  right: PeakRmsPair[];
  stereo: boolean;
}

function computeStaticPeaksRmsFromMono(mono: Float32Array, columns: number): PeakRmsPair[] {
  const cols = Math.max(1, Math.round(columns));
  const length = mono.length;
  if (length === 0) {
    return Array.from({ length: cols }, () => ({ min: 0, max: 0, rms: 0 }));
  }

  const peaks: PeakRmsPair[] = new Array(cols);
  const samplesPerCol = length / cols;

  for (let col = 0; col < cols; col++) {
    const start = Math.floor(col * samplesPerCol);
    const end = col === cols - 1 ? length : Math.floor((col + 1) * samplesPerCol);
    const safeEnd = Math.max(start + 1, Math.min(end, length));

    let lo = Infinity;
    let hi = -Infinity;
    let sumSq = 0;
    const n = safeEnd - start;
    for (let i = start; i < safeEnd; i++) {
      const v = mono[i];
      if (v < lo) lo = v;
      if (v > hi) hi = v;
      sumSq += v * v;
    }
    if (lo === Infinity) { lo = 0; hi = 0; }
    peaks[col] = { min: lo, max: hi, rms: Math.sqrt(sumSq / n) };
  }

  return peaks;
}

export function computeStaticPeaksRms(buffer: AudioBuffer, columns: number): PeakRmsPair[] {
  const cols = Math.max(1, Math.round(columns));
  const numChannels = buffer.numberOfChannels;
  const length = buffer.length;

  if (length === 0 || numChannels === 0) {
    return Array.from({ length: cols }, () => ({ min: 0, max: 0, rms: 0 }));
  }

  // Mono mixdown
  let mono: Float32Array;
  if (numChannels === 1) {
    mono = buffer.getChannelData(0);
  } else {
    mono = new Float32Array(length);
    for (let ch = 0; ch < numChannels; ch++) {
      const data = buffer.getChannelData(ch);
      for (let i = 0; i < length; i++) mono[i] += data[i];
    }
    const inv = 1 / numChannels;
    for (let i = 0; i < length; i++) mono[i] *= inv;
  }

  return computeStaticPeaksRmsFromMono(mono, cols);
}

export function computeStaticPeaksRmsStereo(buffer: AudioBuffer, columns: number): StereoPeakRms {
  const cols = Math.max(1, Math.round(columns));
  const numChannels = buffer.numberOfChannels;
  const length = buffer.length;

  if (length === 0 || numChannels === 0) {
    const empty = Array.from({ length: cols }, () => ({ min: 0, max: 0, rms: 0 }));
    return { left: empty, right: empty, stereo: false };
  }

  if (numChannels === 1) {
    const mono = buffer.getChannelData(0);
    const peaks = computeStaticPeaksRmsFromMono(mono, cols);
    return { left: peaks, right: peaks, stereo: false };
  }

  const left = computeStaticPeaksRmsFromMono(buffer.getChannelData(0), cols);
  const right = computeStaticPeaksRmsFromMono(buffer.getChannelData(1), cols);
  return { left, right, stereo: true };
}
